// HTTP client utilities for API communication

export interface RequestConfig extends RequestInit {
  baseURL?: string;
  timeout?: number;
  params?: Record<string, string | number | boolean>;
}

export interface ApiResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
  headers: Headers;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public response?: Response
  ) {
    super(message);
    this.name = "ApiError";
  }
}

class HttpClient {
  private baseURL: string;
  private defaultHeaders: Record<string, string>;

  constructor(baseURL = "", defaultHeaders: Record<string, string> = {}) {
    this.baseURL = baseURL;
    this.defaultHeaders = {
      "Content-Type": "application/json",
      ...defaultHeaders,
    };
  }

  private buildURL(url: string, params?: Record<string, string | number | boolean>): string {
    const fullURL = url.startsWith("http") ? url : `${this.baseURL}${url}`;

    if (!params) return fullURL;

    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      searchParams.append(key, String(value));
    });

    const separator = fullURL.includes("?") ? "&" : "?";
    return `${fullURL}${separator}${searchParams.toString()}`;
  }

  private async handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
    const contentType = response.headers.get("content-type");
    const isJson = contentType?.includes("application/json");

    let data: T;
    if (isJson) {
      data = await response.json();
    } else {
      data = (await response.text()) as unknown as T;
    }

    if (!response.ok) {
      const message =
        isJson && typeof data === "object" && data && "message" in data
          ? (data as any).message
          : `HTTP ${response.status}: ${response.statusText}`;

      throw new ApiError(message, response.status, response);
    }

    return {
      data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    };
  }

  async request<T = any>(url: string, config: RequestConfig = {}): Promise<ApiResponse<T>> {
    const { baseURL, timeout = 10000, params, headers = {}, ...fetchConfig } = config;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const fullURL = this.buildURL(url, params);
      const response = await fetch(fullURL, {
        ...fetchConfig,
        headers: {
          ...this.defaultHeaders,
          ...headers,
        },
        signal: controller.signal,
      });

      return await this.handleResponse<T>(response);
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        throw new ApiError("Request timeout", 408);
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async get<T = any>(url: string, config?: Omit<RequestConfig, "method" | "body">): Promise<ApiResponse<T>> {
    return this.request<T>(url, { ...config, method: "GET" });
  }

  async post<T = any>(url: string, data?: any, config?: Omit<RequestConfig, "method">): Promise<ApiResponse<T>> {
    return this.request<T>(url, {
      ...config,
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T = any>(url: string, data?: any, config?: Omit<RequestConfig, "method">): Promise<ApiResponse<T>> {
    return this.request<T>(url, {
      ...config,
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T = any>(url: string, data?: any, config?: Omit<RequestConfig, "method">): Promise<ApiResponse<T>> {
    return this.request<T>(url, {
      ...config,
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T = any>(url: string, config?: Omit<RequestConfig, "method" | "body">): Promise<ApiResponse<T>> {
    return this.request<T>(url, { ...config, method: "DELETE" });
  }

  setAuthToken(token: string) {
    this.defaultHeaders.Authorization = `Bearer ${token}`;
  }

  removeAuthToken() {
    delete this.defaultHeaders.Authorization;
  }

  setBaseURL(baseURL: string) {
    this.baseURL = baseURL;
  }
}

// Create default instance
export const httpClient = new HttpClient();

// Export factory function for creating custom instances
export function createHttpClient(baseURL?: string, defaultHeaders?: Record<string, string>) {
  return new HttpClient(baseURL, defaultHeaders);
}

// Convenience functions using default instance
export const api = {
  get: <T = any>(url: string, config?: Omit<RequestConfig, "method" | "body">) => httpClient.get<T>(url, config),
  post: <T = any>(url: string, data?: any, config?: Omit<RequestConfig, "method">) =>
    httpClient.post<T>(url, data, config),
  put: <T = any>(url: string, data?: any, config?: Omit<RequestConfig, "method">) =>
    httpClient.put<T>(url, data, config),
  patch: <T = any>(url: string, data?: any, config?: Omit<RequestConfig, "method">) =>
    httpClient.patch<T>(url, data, config),
  delete: <T = any>(url: string, config?: Omit<RequestConfig, "method" | "body">) => httpClient.delete<T>(url, config),
  setAuthToken: (token: string) => httpClient.setAuthToken(token),
  removeAuthToken: () => httpClient.removeAuthToken(),
  setBaseURL: (baseURL: string) => httpClient.setBaseURL(baseURL),
};
